import { useState } from 'react'
import firebase from '../../firebase/compat'
import { Link } from 'react-router-dom'
import { client } from '../../hooks/Client'
import useAddressMatches from '../../hooks/useAddressMatches'
import useSharedCollection from '../../firebase/useSharedCollection'
import { useFirestoreQuery } from '../../firebase/useFirestore'
import describeAddressMatch, { addressText } from '../../helpers/addressMatching/describeAddressMatch'
import householdChoices, { HOUSEHOLD_CHOICE_FIELDS } from '../../helpers/addressMatching/householdChoices'
import { invalidateResearchAggregate } from '../../hooks/measurementSignals/researchAggregateCache'
import StaticCardLabel from './StaticCardLabel'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md

const ACTION_LABELS = {
    'new-traject': 'Nieuw traject',
    create: 'Nieuw huishouden',
    link: 'Koppelen',
}

/**
 * Eén twijfelgeval met zijn keuzes. Een eigen component (niet in de render van
 * de kaart gedefinieerd) zodat elke rij zijn eigen keuze en foutmelding houdt.
 * AI-CONTEXT: See docs/pitfalls.md#state-component-defined-in-render
 *
 * @param {object} props
 * @param {object} props.match - Het AddressMatches-document
 * @param {Array<object>} props.households - Huishoudens om aan te koppelen ([] als niet nodig)
 * @param {Array<object>|string} props.addressRows - PersonaData-adresregels van die huishoudens ('' zolang ze laden)
 * @param {(adressId: string) => void} props.onResolved - Na een geslaagde keuze; '' bij negeren
 */
const AddressMatchRow = ({ match, households, addressRows, onResolved }) => {
    // null = nog niets gekozen: dan geldt het voorstel (het enige huishouden op
    // hetzelfde adres), ook als de adresregels pas later binnenkomen.
    const [chosen, setChosen] = useState(/** @type {string|null} */ (null))
    const [saving, setSaving] = useState(false)
    const [failed, setFailed] = useState(false)

    const { reason, actions, needsHousehold } = describeAddressMatch(match)

    // Huishoudens op hetzelfde adres (BAG-id of getypt adres) eerst, met een
    // leesbare naam. AI-CONTEXT: See src/helpers/addressMatching/householdChoices.js
    const { options, suggested } = householdChoices(households, addressRows, match)
    // Half gekoppeld: een eerdere keuze legde het huishouden al vast en stopte
    // halverwege. De server laat dan alleen afmaken met dát huishouden
    // (/complete 2026-10-14), dus de kaart ook.
    const partial = match.AdressID || ''
    const selected = partial || (chosen ?? suggested)

    const choose = async (action) => {
        if (saving) return
        setSaving(true)
        setFailed(false)
        try {
            const resolve = firebase.app().functions('europe-west1').httpsCallable('resolveAddressMatch')
            const { data } = await resolve({
                client,
                matchId: match.ID,
                action,
                ...(action === 'link' ? { adressId: selected } : {}),
            })
            onResolved(data?.adressId || '')
            // Geen eigen opruiming: het geval staat daarna op 'resolved' en
            // valt vanzelf uit de lijst bij de volgende snapshot.
        } catch (error) {
            console.error('Twijfelgeval afhandelen mislukt', error)
            setFailed(true)
        } finally {
            setSaving(false)
            // De antwoorden horen nu (deels) bij een huishouden: de pagina die je
            // hierna opent mag niet de stand uit de cache van vóór het koppelen tonen
            // (testronde 2026-10-13, B13). Ook na een fout: de server koppelt in brokken.
            invalidateResearchAggregate(match.ResearchID)
        }
    }

    const ignoreLabel = match.Reason === 'completed-restart' ? 'Dubbel, negeren' : 'Negeren'

    return (
        <li className='address-match-row'>
            <p className='address-match-address'><b>{addressText(match)}</b></p>
            <p className='address-match-reason'>{reason}</p>

            {needsHousehold && (
                <select
                    aria-label='Kies een huishouden'
                    value={selected}
                    onChange={(e) => setChosen(e.target.value)}
                    disabled={saving || Boolean(partial)}
                >
                    <option value=''>Kies een huishouden</option>
                    {options.map((o) => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                </select>
            )}

            <div className='address-match-actions'>
                {actions.map((action) => (
                    <button
                        key={action}
                        type='button'
                        className={action === 'ignore' ? 'button-simple' : ''}
                        disabled={saving || (action === 'link' && !selected) || (action === 'ignore' && Boolean(partial))}
                        onClick={() => choose(action)}
                    >
                        {action === 'ignore' ? ignoreLabel : ACTION_LABELS[action]}
                    </button>
                ))}
            </div>

            {partial && (
                <p className='measurement-signal-note'>
                    Een eerdere keuze stopte halverwege. Kies opnieuw om hem af te maken; negeren kan niet meer.
                </p>
            )}

            {failed && <p className='measurement-signal-warning' role='alert'>Dit lukte niet. Probeer het opnieuw.</p>}
        </li>
    )
}

/**
 * Adressen om te koppelen: invullingen en aanmeldingen waarvan de software niet
 * zeker weet bij welk huishouden ze horen. Alleen twijfelgevallen; een gewoon
 * nieuw of lopend adres wordt vanzelf gekoppeld.
 */
const DashboardAddressMatchesCard = () => {
    const { matches, loaded, error } = useAddressMatches()

    // De huishoudens pas ophalen als een geval om een keuze vraagt.
    const needsHouseholds = matches.some((m) => describeAddressMatch(m).needsHousehold)
    const households = useSharedCollection(needsHouseholds ? 'Adresses' : '')
    // Adresregels voor de namen in de keuzelijst en om een onbekend adres op het
    // getypte adres te herkennen — ook alleen als een geval erom vraagt.
    const addressRows = useFirestoreQuery('PersonaData', {
        where: [['Title', 'in', HOUSEHOLD_CHOICE_FIELDS]],
        enabled: needsHouseholds,
    })
    // Na een keuze verdwijnt de rij; zeg dan waar het huishouden staat.
    const [lastAdressId, setLastAdressId] = useState('')
    const doneNote = lastAdressId ? (
        <p className='measurement-signal-note'>
            Gekoppeld. <Link to={`/${client}/adressdetails/${lastAdressId}`}>Open het huishouden</Link>
        </p>
    ) : null

    const title = <StaticCardLabel title='Adressen om te koppelen' pill={{ variant: 'housevisits', label: 'Huishoudens' }} />

    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (!loaded) {
        return <div>{title}<p className='score-explain-card__placeholder'>Laden…</p></div>
    }

    if (error) {
        return (
            <div>
                {title}
                <p className='measurement-signal-warning' role='alert'>
                    De lijst kon niet worden geladen. Er kunnen adressen wachten die hier niet staan.
                </p>
            </div>
        )
    }

    if (matches.length === 0) {
        return (
            <div>
                {title}
                {doneNote}
                <p className='measurement-signal-note'>Er wachten geen adressen op een keuze.</p>
            </div>
        )
    }

    return (
        <div>
            {title}
            {doneNote}
            <ul className='address-match-list'>
                {matches.map((match) => (
                    <AddressMatchRow
                        key={match.ID}
                        match={match}
                        households={households || []}
                        addressRows={addressRows}
                        onResolved={setLastAdressId}
                    />
                ))}
            </ul>
        </div>
    )
}

export default DashboardAddressMatchesCard
